<template>
  <div class="settings-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <div class="settings-container">
      <!-- Header -->
      <div class="settings-header">
        <div class="header-content">
          <h1 class="header-title">Manage My Settings</h1>
          <p class="header-subtitle">Personalize your experience and keep your profile up to date.</p>
        </div>
        <button class="btn-back" @click="$router.push('/staff-dashboard')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back to Dashboard
        </button>
      </div>

      <div class="settings-grid">
        <!-- LEFT SIDEBAR -->
        <div class="sidebar-card">
          <!-- Avatar Section -->
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <div v-if="photoURL" class="avatar" :style="{ backgroundImage: `url(${photoURL})` }"></div>
              <div v-else class="avatar avatar-placeholder" :style="{ background: avatarGradient }">
                {{ initials }}
              </div>
              <button class="avatar-edit" @click="$refs.avatarInput.click()">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M11.3333 2.00001C11.5084 1.82491 11.716 1.68602 11.9441 1.59126C12.1722 1.49649 12.4165 1.44769 12.6633 1.44769C12.9101 1.44769 13.1544 1.49649 13.3825 1.59126C13.6106 1.68602 13.8183 1.82491 13.9933 2.00001C14.1684 2.17511 14.3073 2.38276 14.4021 2.61088C14.4968 2.83899 14.5456 3.08329 14.5456 3.33001C14.5456 3.57673 14.4968 3.82103 14.4021 4.04914C14.3073 4.27726 14.1684 4.48491 13.9933 4.66001L5.00001 13.6533L1.33334 14.6667L2.34668 11L11.3333 2.00001Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                @change="updateAvatar"
                style="display: none"
              />
            </div>
            <div class="user-info">
              <h2 class="user-name">{{ displayName || 'User' }}</h2>
              <p class="user-email">{{ email }}</p>
            </div>
          </div>

          <!-- Points Card -->
          <div class="points-card">
            <div class="points-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="points-content">
              <div class="points-label">Your Points</div>
              <div class="points-value">{{ points }}</div>
              <div class="points-today">+{{ pointsToday }} today</div>
              <div class="points-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                </div>
                <div class="progress-label">Next badge in {{ pointsToNextBadge }} pts</div>
              </div>
            </div>
          </div>

          <!-- Activity Card -->
          <div class="activity-card">
            
          </div>
        </div>

        <!-- RIGHT CONTENT -->
        <div class="content-card">
          <!-- Tabs -->
          <div class="tabs-header">
            <button
              v-for="(tabItem, idx) in tabs"
              :key="idx"
              :class="['tab-button', { active: activeTab === idx }]"
              @click="activeTab = idx"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" v-html="tabItem.icon"></svg>
              <span>{{ tabItem.label }}</span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- PROFILE TAB -->
            <div v-if="activeTab === 0" class="tab-panel">
              <form @submit.prevent="saveProfile" class="settings-form">
                <div class="form-group">
                  <label>Full Name</label>
                  <input
                    v-model="profileForm.displayName"
                    type="text"
                    placeholder="Enter your full name"
                  />
                </div>

                <div class="form-group">
                  <label>Email</label>
                  <input
                    v-model="profileForm.email"
                    type="email"
                    disabled
                  />
                  <p class="form-hint">Email cannot be changed</p>
                </div>

                <div class="form-group">
                  <label>Phone Number</label>
                  <input
                    v-model="profileForm.phone"
                    type="tel"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div class="form-group">
                  <label>Address</label>
                  <textarea
                    v-model="profileForm.address"
                    rows="3"
                    placeholder="Enter your address"
                  ></textarea>
                </div>

                <button type="submit" class="btn-save" :disabled="savingProfile">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 2.5H5C4.17157 2.5 3.5 3.17157 3.5 4V16C3.5 16.8284 4.17157 17.5 5 17.5H15C15.8284 17.5 16.5 16.8284 16.5 16V4C16.5 3.17157 15.8284 2.5 15 2.5Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M12.5 2.5V6.5H7.5V2.5" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M7.5 11H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M7.5 14H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ savingProfile ? 'Saving...' : 'Save Changes' }}
                </button>
              </form>
            </div>

            <!-- SECURITY TAB -->
            <div v-if="activeTab === 1" class="tab-panel">
              <form @submit.prevent="changePassword" class="settings-form">
                <div class="security-notice">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div>
                    <div class="notice-title">Secure Your Account</div>
                    <div class="notice-text">Keep your password strong and unique</div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Current Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.currentPassword"
                      :type="showCurrentPassword ? 'text' : 'password'"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showCurrentPassword = !showCurrentPassword"
                    >
                      <svg v-if="showCurrentPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label>New Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.newPassword"
                      :type="showNewPassword ? 'text' : 'password'"
                      placeholder="Enter new password"
                      @input="checkPasswordStrength"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showNewPassword = !showNewPassword"
                    >
                      <svg v-if="showNewPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="passwordForm.newPassword" class="password-strength">
                    <div class="strength-bar">
                      <div
                        class="strength-fill"
                        :style="{ width: passwordStrength.percent + '%', background: passwordStrength.color }"
                      ></div>
                    </div>
                    <span class="strength-label" :style="{ color: passwordStrength.color }">
                      {{ passwordStrength.label }}
                    </span>
                  </div>
                </div>

                <div class="form-group">
                  <label>Confirm New Password</label>
                  <input
                    v-model="passwordForm.confirmPassword"
                    :type="showNewPassword ? 'text' : 'password'"
                    placeholder="Confirm new password"
                  />
                </div>

                <div v-if="passwordError" class="form-error">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M8 5V8.5M8 11H8.005" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ passwordError }}
                </div>

                <button type="submit" class="btn-save" :disabled="changingPassword">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 8.75V6.25C15 5.91848 14.8683 5.60054 14.6339 5.36612C14.3995 5.1317 14.0815 5 13.75 5H6.25C5.91848 5 5.60054 5.1317 5.36612 5.36612C5.1317 5.60054 5 5.91848 5 6.25V13.75C5 14.0815 5.1317 14.3995 5.36612 14.6339C5.60054 14.8683 5.91848 15 6.25 15H8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10 10H16.25M16.25 10L14.375 8.125M16.25 10L14.375 11.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ changingPassword ? 'Changing...' : 'Change Password' }}
                </button>
              </form>

              <!-- Two-Factor Authentication -->
              <div class="security-section">
                <div class="section-header">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="snack.show" class="toast-notification">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { auth, db, storage } from '@/firebase'
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
  name: 'StaffSettings',
  data() {
    return {
      activeTab: 0,
      tabs: [
        {
          label: 'Profile',
          icon: '<path d="M10 11.6667C12.7614 11.6667 15 9.42811 15 6.66667C15 3.90524 12.7614 1.66667 10 1.66667C7.23858 1.66667 5 3.90524 5 6.66667C5 9.42811 7.23858 11.6667 10 11.6667Z" stroke="currentColor" stroke-width="1.5"/><path d="M3.33334 18.3333C3.33334 16.5652 4.03572 14.8695 5.28596 13.6193C6.5362 12.369 8.23189 11.6667 10 11.6667C11.7681 11.6667 13.4638 12.369 14.7141 13.6193C15.9643 14.8695 16.6667 16.5652 16.6667 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
        },
        {
          label: 'Security',
          icon: '<path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33334 4.16667V10C3.33334 15 10 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
        }
      ],

      // User data
      email: '',
      displayName: '',
      photoURL: '',
      points: 1368,
      pointsToday: 42,
      pointsToNextBadge: 32,

      // Profile form
      profileForm: {
        displayName: '',
        email: '',
        phone: '',
        address: ''
      },
      savingProfile: false,

      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      changingPassword: false,
      passwordError: '',
      passwordStrength: {
        percent: 0,
        label: '',
        color: ''
      },

      // Two-factor
      twoFactorEnabled: false,

      // Activity data
      activityData: [12, 19, 8, 15, 22, 18, 25, 20, 28, 24, 30, 26, 32, 28],
      stats: {
        calls: 47,
        emails: 128,
        meetings: 23
      },

      // UI
      snack: { show: false, msg: '' }
    }
  },

  computed: {
    initials() {
      return this.displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    avatarGradient() {
      return 'linear-gradient(135deg, #00fff5, #7b2ff7)'
    },
    progressPercent() {
      return Math.min((this.points % 100) / 100 * 100, 100)
    },
    activityPoints() {
      const width = 280
      const height = 80
      const padding = 10
      const points = this.activityData.map((val, i) => {
        const x = (i / (this.activityData.length - 1)) * (width - padding * 2) + padding
        const y = height - ((val / 35) * (height - padding * 2)) - padding
        return `${x},${y}`
      })
      return points.join(' ')
    }
  },

  async created() {
    const user = auth.currentUser
    if (!user) {
      this.$router.push('/')
      return
    }

    this.email = user.email
    this.displayName = user.displayName || 'User'
    this.photoURL = user.photoURL || ''

    this.profileForm.displayName = this.displayName
    this.profileForm.email = this.email

    // Load additional user data from Firestore
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        this.profileForm.phone = data.phone || ''
        this.profileForm.address = data.address || ''
        this.points = data.points || 1368
        this.twoFactorEnabled = data.twoFactorEnabled || false
      }
    } catch (e) {
      console.error('Error loading user data:', e)
    }
  },

  methods: {
    async updateAvatar(e) {
      const file = e.target.files[0]
      if (!file) return

      try {
        const user = auth.currentUser
        const path = `avatars/${user.uid}/${Date.now()}_${file.name}`
        const storageRef = sRef(storage, path)
        await uploadBytes(storageRef, file)
        const url = await getDownloadURL(storageRef)

        await updateProfile(user, { photoURL: url })
        this.photoURL = url

        this.snack = { show: true, msg: '✓ Avatar updated' }
      } catch (e) {
        console.error('Avatar update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update avatar' }
      }
    },

    async saveProfile() {
      this.savingProfile = true
      try {
        const user = auth.currentUser

        // Update auth profile
        await updateProfile(user, {
          displayName: this.profileForm.displayName
        })

        // Update Firestore
        await setDoc(doc(db, 'users', user.uid), {
          displayName: this.profileForm.displayName,
          email: this.profileForm.email,
          phone: this.profileForm.phone,
          address: this.profileForm.address,
          updatedAt: new Date()
        }, { merge: true })

        this.displayName = this.profileForm.displayName
        this.snack = { show: true, msg: '✓ Profile updated successfully' }
      } catch (e) {
        console.error('Profile update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update profile' }
      } finally {
        this.savingProfile = false
      }
    },

    checkPasswordStrength() {
      const password = this.passwordForm.newPassword
      if (!password) {
        this.passwordStrength = { percent: 0, label: '', color: '' }
        return
      }

      let strength = 0
      if (password.length >= 8) strength += 25
      if (password.length >= 12) strength += 25
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 12.5
      if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5

      let label = 'Weak'
      let color = '#f87171'
      if (strength >= 75) {
        label = 'Strong'
        color = '#4ade80'
      } else if (strength >= 50) {
        label = 'Medium'
        color = '#ffbe0b'
      }

      this.passwordStrength = { percent: strength, label, color }
    },

    async changePassword() {
      this.passwordError = ''

      if (!this.passwordForm.currentPassword || !this.passwordForm.newPassword) {
        this.passwordError = 'Please fill in all fields'
        return
      }

      if (this.passwordForm.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = 'Passwords do not match'
        return
      }

      this.changingPassword = true
      try {
        const user = auth.currentUser
        const credential = EmailAuthProvider.credential(
          user.email,
          this.passwordForm.currentPassword
        )

        await reauthenticateWithCredential(user, credential)
        await updatePassword(user, this.passwordForm.newPassword)

        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
        this.snack = { show: true, msg: '✓ Password changed successfully' }
      } catch (e) {
        console.error('Password change error:', e)
        if (e.code === 'auth/wrong-password') {
          this.passwordError = 'Current password is incorrect'
        } else {
          this.passwordError = 'Failed to change password'
        }
      } finally {
        this.changingPassword = false
      }
    },

    async toggleTwoFactor() {
      try {
        const user = auth.currentUser
        const newStatus = !this.twoFactorEnabled

        await updateDoc(doc(db, 'users', user.uid), {
          twoFactorEnabled: newStatus
        })

        this.twoFactorEnabled = newStatus
        this.snack = {
          show: true,
          msg: newStatus ? '✓ 2FA enabled' : '✓ 2FA disabled'
        }
      } catch (e) {
        console.error('2FA toggle error:', e)
        this.snack = { show: true, msg: '✗ Failed to update 2FA' }
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700;800&display=swap');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.settings-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 20% 20%, rgba(123, 47, 247, 0.3), transparent 45%),
    radial-gradient(circle at 80% 80%, rgba(255, 0, 110, 0.3), transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(0, 255, 245, 0.2), transparent 50%),
    linear-gradient(135deg, #1a0b2e 0%, #0a0118 50%, #1a0b2e 100%);
  background-size: 200% 200%;
  animation: bg-breathe 25s ease-in-out infinite;
  position: relative;
  font-family: 'Sora', sans-serif;
  padding: 40px 0 80px;
}

@keyframes bg-breathe {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.page-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(0, 255, 245, 0.08) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(123, 47, 247, 0.08) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
  animation: grid-flow 35s linear infinite;
  pointer-events: none;
}

@keyframes grid-flow {
  0% { transform: translate(0, 0); }
  100% { transform: translate(60px, 60px); }
}

.page-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.5;
  animation: orb-float 18s ease-in-out infinite;
  mix-blend-mode: screen;
  pointer-events: none;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, #00fff5, transparent);
  top: -200px;
  right: -200px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #ff006e, transparent);
  bottom: -150px;
  left: -150px;
  animation-delay: 6s;
}

@keyframes orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(50px, -50px) scale(1.1); }
  66% { transform: translate(-40px, 40px) scale(0.9); }
}

.settings-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

/* Header */
.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
  padding: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #00fff5;
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(0, 255, 245, 0.3);
}

.header-title {
  font-size: 32px;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.header-subtitle {
  font-size: 16px;
  color: #b8b8d1;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
  transform: translateX(-4px);
}

/* Grid */
.settings-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  align-items: start;
}

/* Sidebar */
.sidebar-card {
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.avatar-section {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #7b2ff7;
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(123, 47, 247, 0.3);
  padding: 32px;
  text-align: center;
}

.avatar-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 4px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  font-weight: 900;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
}

.avatar-edit {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border: 2px solid #ffffff;
  border-radius: 50%;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 255, 245, 0.4);
}

.avatar-edit:hover {
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 25px rgba(0, 255, 245, 0.6);
}

.user-name {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4px;
}

.user-email {
  font-size: 14px;
  color: #b8b8d1;
}

/* Points Card */
.points-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #ffbe0b;
  border-radius: 16px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(255, 190, 11, 0.3);
  padding: 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.points-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffbe0b, #fb5607);
  border-radius: 12px;
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(255, 190, 11, 0.4);
}

.points-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Space Mono', monospace;
  margin-bottom: 6px;
}

.points-value {
  font-size: 36px;
  font-weight: 900;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
  line-height: 1;
  margin-bottom: 4px;
}

.points-today {
  font-size: 13px;
  color: #4ade80;
  font-weight: 600;
  margin-bottom: 12px;
}

.progress-bar {
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00fff5, #7b2ff7);
  border-radius: 3px;
  transition: width 0.5s ease;
}

.progress-label {
  font-size: 11px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
}

/* Activity Card */
.activity-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #ff006e;
  border-radius: 16px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(255, 0, 110, 0.3);
  padding: 24px;
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
}

.activity-header svg {
  color: #ff006e;
  filter: drop-shadow(0 0 8px #ff006e);
}

.activity-chart {
  margin-bottom: 16px;
}

.activity-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-label {
  font-size: 11px;
  color: #b8b8d1;
  font-family: 'Space Mono', monospace;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Space Mono', monospace;
}

.stat-divider {
  color: rgba(255, 255, 255, 0.3);
  font-size: 20px;
}

/* Content Card */
.content-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid #00fff5;
  border-radius: 20px;
  backdrop-filter: blur(20px);
  box-shadow: 0 0 40px rgba(0, 255, 245, 0.3);
  overflow: hidden;
}

/* Tabs */
.tabs-header {
  display: flex;
  background: linear-gradient(90deg, #7b2ff7, #ff006e);
  padding: 8px;
  gap: 8px;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  background: transparent;
  border: none;
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.tab-button.active {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.tab-content {
  padding: 40px;
}

.tab-panel {
  animation: fade-in 0.3s ease;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Forms */
.settings-form {
  max-width: 600px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Space Mono', monospace;
  margin-bottom: 8px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #ffffff;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.form-group input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.form-group input:focus,
.form-group textarea:focus {
  background: rgba(255, 255, 255, 0.12);
  border-color: #00fff5;
  box-shadow: 0 0 20px rgba(0, 255, 245, 0.3);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-hint {
  font-size: 12px;
  color: #b8b8d1;
  margin-top: 6px;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #f87171;
  font-size: 14px;
  margin-bottom: 20px;
}

.password-input {
  position: relative;
}

.btn-toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-toggle-password:hover {
  color: #ffffff;
}

.password-strength {
  margin-top: 12px;
}

.strength-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 6px;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.strength-label {
  font-size: 12px;
  font-weight: 600;
  font-family: 'Space Mono', monospace;
}

.btn-save {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #00fff5, #7b2ff7);
  border: none;
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 255, 245, 0.4);
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(0, 255, 245, 0.6);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Security */
.security-notice {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(0, 255, 245, 0.1);
  border: 2px solid rgba(0, 255, 245, 0.3);
  border-radius: 12px;
  margin-bottom: 32px;
}

.security-notice svg {
  color: #00fff5;
  filter: drop-shadow(0 0 8px #00fff5);
  flex-shrink: 0;
}

.notice-title {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4px;
}

.notice-text {
  font-size: 14px;
  color: #b8b8d1;
}

.security-section {
  max-width: 600px;
  margin-top: 40px;
  padding-top: 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
}

.section-header p {
  font-size: 14px;
  color: #b8b8d1;
  margin-bottom: 20px;
}

.twofa-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.twofa-card:hover {
  border-color: #7b2ff7;
  box-shadow: 0 0 20px rgba(123, 47, 247, 0.3);
}

.twofa-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7b2ff7, #ff006e);
  border-radius: 12px;
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(123, 47, 247, 0.4);
}

.twofa-content {
  flex: 1;
}

.twofa-status {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 4px;
}

.twofa-description {
  font-size: 13px;
  color: #b8b8d1;
}

.btn-twofa {
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-twofa:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: #00fff5;
  box-shadow: 0 0 15px rgba(0, 255, 245, 0.3);
}

.btn-twofa.enabled {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.btn-twofa.enabled:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: #f87171;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.3);
}

/* Toast */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(123, 47, 247, 0.95), rgba(255, 0, 110, 0.95));
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  z-index: 1001;
  animation: toast-in 0.3s ease;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-notification button {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  transition: transform 0.2s ease;
}

.toast-notification button:hover {
  transform: scale(1.2);
}

/* Responsive */
@media (max-width: 1200px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }

  .sidebar-card {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .settings-header {
    flex-direction: column;
    text-align: center;
  }

  .header-title {
    font-size: 24px;
  }

  .tab-content {
    padding: 24px;
  }

  .settings-form,
  .security-section {
    max-width: 100%;
  }

  .activity-stats {
    flex-wrap: wrap;
  }
}
</style>